import { FC } from 'react';
import {
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Upload,
	UploadFile,
} from 'antd';
import rule, { rules } from '@/libs/antd/rules';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { CustomerCompanyManagementInformationForm } from '@/pages/frm/customer-v2/CustomerPage';
import {
	useSelectAddress,
	useSelectCitizenship,
	useSelectCountry,
	useSelectGeneralStatus,
	useSelectIdentityType,
	useSelectManagementPosition,
} from '@/libs/fleet-api/hooks/master-data';
import { ShareholderType } from '@/libs/fleet-api/enums';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { inputKitas, inputKtp, inputPassport } from '@/libs/antd/input';
import { FileData } from '@/libs/fleet-api/services/types';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { inputRtRw } from '@/libs/antd/input-number';
import { DefaultOptionType } from 'antd/es/select';
import { MasterData } from '@/libs/fleet-api/services/master-data';

export const useManagerForm = (
	onSubmit: (values: CustomerCompanyManagementInformationForm) => void,
) => {
	const forms = useForm<CustomerCompanyManagementInformationForm>();

	return {
		...forms,
		onSubmit,
	};
};

const ManagerForm: FC<ReturnType<typeof useManagerForm>> = ({
	form,
	value,
	fieldName,
	onSubmit,
}) => {
	const selectCitizenship = useSelectCitizenship();
	const selectCountry = useSelectCountry();
	const selectPosition = useSelectManagementPosition();
	const selectAddress = useSelectAddress();
	const selectStatus = useSelectGeneralStatus();
	const selectIdentityCardType = useSelectIdentityType(
		ShareholderType.Perorangan,
	);

	const getSelectCountryOption = () => {
		if (value?.citizenship?.id === 1) {
			return [
				{
					value: 1,
					label: 'Indonesia',
				},
			] as DefaultOptionType[];
		}

		if (!value?.nationality?.id || selectCountry.options?.length) {
			return selectCountry.options?.filter((data) => data.value !== 1);
		}

		return [
			{
				value: value?.nationality?.id,
				label: value?.nationality?.value,
			},
		] as DefaultOptionType[];
	};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			onFinish={onSubmit}
			style={{ marginTop: 20 }}
		>
			<Form.Item
				name={fieldName('id')}
				noStyle
			/>
			<Form.Item
				name={fieldName('isShareholder')}
				noStyle
			/>
			<Form.Item
				name={fieldName('shareholding')}
				noStyle
			/>
			<Form.Item
				name={fieldName('shareholderId')}
				noStyle
			/>
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('name')}
						label="Nama"
						rules={[rule.required(), rule.charOnly()]}
					>
						<Input />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('citizenship')}
						label="Kewarganegaraan"
						rules={[rule.required()]}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.value,
								},
							};
						}}
					>
						<Select
							{...selectCitizenship}
							onSelect={(value, option) => {
								selectCitizenship.onSelect!(value, option);
								form.resetFields([
									fieldName('nationality'),
									fieldName('identityCardType'),
								]);
							}}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('nationality')}
						label="Nationality"
						rules={[rule.required()]}
						getValueFromEvent={(_, option: DefaultOptionType) => {
							return {
								id: option.value,
								value: option.label,
							} as MasterData;
						}}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.name,
								},
							};
						}}
					>
						<Select
							{...selectCountry}
							options={getSelectCountryOption()}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('identityCardType')}
						label="Jenis Identitas"
						rules={[rule.required()]}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.value,
								},
							};
						}}
					>
						<Select
							{...selectIdentityCardType}
							options={
								value?.citizenship?.id === 2
									? selectIdentityCardType.options?.filter(
											(option) => option.value !== 1,
										)
									: selectIdentityCardType.options
							}
							onSelect={(val, option) => {
								selectIdentityCardType.onSelect!(val, option);
							}}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					{value?.identityCardType?.id === 1 && (
						<Form.Item
							name={fieldName('identityNumber')}
							label="No. ID"
							rules={rules.ktp}
						>
							<Input {...inputKtp} />
						</Form.Item>
					)}
					{value?.identityCardType?.id === 2 && (
						<Form.Item
							name={fieldName('identityNumber')}
							label="No. ID"
							rules={rules.passport}
						>
							<Input {...inputPassport} />
						</Form.Item>
					)}
					{value?.identityCardType?.id === 3 && (
						<Form.Item
							name={fieldName('identityNumber')}
							label="No. ID"
							rules={rules.kitas}
						>
							<Input {...inputKitas} />
						</Form.Item>
					)}
				</Col>
				<Col span={12}>
					<Form.Item
						label="Upload Identitas"
						name={fieldName('identityFile')}
						rules={[rule.required()]}
						getValueProps={(value) => {
							const file = value as FileData;
							return {
								fileList: file?.id
									? ([
											{
												uid: file.id,
												name: file.name,
												thumbUrl: file.url,
											},
										] as UploadFile[])
									: [],
							};
						}}
						normalize={({ file }: { file: UploadFile }) => {
							return {
								id: file.uid,
								format: file.type,
								name: file.name,
								url: file.thumbUrl,
							} as FileData;
						}}
					>
						<Upload.Dragger
							listType="picture"
							accept=".jpg,.jpeg,.png"
							maxCount={1}
							beforeUpload={() => false}
							onRemove={() => {
								form.setFieldValue(fieldName('identityFile'), null);
								return false;
							}}
							onChange={async ({ file }) => {
								const fileid = Date.now().toString();
								const customerId = Date.now();
								const filepath = customerId
									? `customer/${customerId}/management-document`
									: 'customer/temp/management-document';

								try {
									const url = await uploadMutation.mutateAsync({
										file: file as unknown as File,
										fileid,
										filepath,
									});

									form.setFieldValue(fieldName('identityFile'), {
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									});
								} catch {
									form.setFieldValue(fieldName('identityFile'), null);
								}
							}}
						>
							{uploadMutation.isPending ? (
								<LoadingOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							) : (
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							)}
						</Upload.Dragger>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('dateOfBirth')}
						label="Tanggal Lahir"
						rules={[rule.required()]}
						getValueProps={(value) => ({
							value: value && dayjs(value),
						})}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
					>
						<DatePicker
							disabledDate={(now) => now > dayjs().subtract(1, 'day')}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('positionId')}
						label="Jabatan"
						rules={[rule.required()]}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.value,
								},
							};
						}}
					>
						<Select {...selectPosition} />
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('address')}
						label="Alamat"
						rules={[rule.required()]}
					>
						<Input.TextArea rows={3} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="RT/RW"
						required
					>
						<Row
							wrap={false}
							align="middle"
							gutter={8}
						>
							<Col flex="auto">
								<Form.Item
									name={fieldName('rt')}
									noStyle
									rules={rules.rt}
								>
									<Input {...inputRtRw} />
								</Form.Item>
							</Col>
							<Col flex="none">/</Col>
							<Col flex="auto">
								<Form.Item
									name={fieldName('rw')}
									noStyle
									rules={rules.rw}
								>
									<Input {...inputRtRw} />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('postal')}
						label="Kode POS"
						rules={[rule.required()]}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.postalId,
									label: value.postalCode,
								},
							};
						}}
					>
						<Select {...selectAddress} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Kelurahan"
						shouldUpdate
					>
						<Input
							disabled
							value={value?.postal?.locationName}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Kecamatan"
						shouldUpdate
					>
						<Input
							disabled
							value={value?.postal?.districtName}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Kota"
						shouldUpdate
					>
						<Input
							disabled
							value={value?.postal?.cityName}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('phoneNumber')}
						label="No. Telepon"
						validateTrigger={['onBlur', 'onFocus']}
						rules={[rule.strNumber(), rule.phoneNumber()]}
					>
						<Input maxLength={13} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('status')}
						label="Status"
						rules={[rule.required()]}
						getValueFromEvent={(_, option) => option?.data}
						getValueProps={(value) => {
							if (!value) return {};
							return {
								value: {
									value: value.id,
									label: value.value,
								},
							};
						}}
					>
						<Select {...selectStatus} />
					</Form.Item>
				</Col>
			</Row>
		</Form>
	);
};

export default ManagerForm;
